import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, UserPlus, IdCard, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ContactTagSetting — Privacy tab entry.
 *
 * Your contact tag is a stable 10-char URL-safe handle that any other
 * user can paste into the "Add by tag" box to link up with you as
 * mutual contacts. Think of it like a phone number for this app —
 * shareable, but only as useful as the person you shared it with.
 */
export const ContactTagSetting = () => {
  const [tag, setTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [peerTag, setPeerTag] = useState('');
  const [adding, setAdding] = useState(false);
  const copyTimer = useRef(null);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/users/me/contact-tag`);
        if (!ignore) setTag(data?.contact_tag || null);
      } catch (e) {
        if (!ignore) toast.error('Could not load your contact tag.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; if (copyTimer.current) clearTimeout(copyTimer.current); };
  }, []);

  const handleCopy = async () => {
    if (!tag) return;
    try {
      await navigator.clipboard.writeText(tag);
      setCopied(true);
      toast.success('Tag copied to clipboard');
      copyTimer.current = setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error('Clipboard unavailable — tap the tag to select it.');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const clean = (peerTag || '').trim();
    if (!clean) return;
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/contacts/add-by-tag`, { tag: clean });
      toast.success(`Added ${data?.username || 'contact'} — they're now in your chat list.`);
      setPeerTag('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add contact.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="contact-tag-setting">
      <div className="space-y-2">
        <Label className="text-sm text-slate-400 flex items-center gap-2">
          <IdCard className="w-3.5 h-3.5" />
          Your contact tag
        </Label>
        <div className="p-4 rounded-lg bg-secondary/30 border border-slate-800">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <code
                  data-testid="contact-tag-value"
                  className="flex-1 font-mono text-lg tracking-[0.2em] text-slate-100 select-all break-all"
                >
                  {tag || '—'}
                </code>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleCopy}
                  disabled={!tag}
                  data-testid="contact-tag-copy-btn"
                  className="flex-shrink-0"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                Share this tag with someone you want to chat with. When they paste it into
                their "Add by tag" field, you'll both land in each other's contacts
                instantly — no invite codes, no mutual approval.
              </p>
            </>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="peer-tag-input" className="text-sm text-slate-400 flex items-center gap-2">
          <UserPlus className="w-3.5 h-3.5" />
          Add someone by tag
        </Label>
        <form onSubmit={handleAdd} className="flex gap-2" data-testid="contact-tag-add-form">
          <Input
            id="peer-tag-input"
            value={peerTag}
            onChange={(e) => setPeerTag(e.target.value)}
            placeholder="Paste their contact tag here"
            autoComplete="off"
            data-testid="contact-tag-peer-input"
            className="font-mono tracking-wider"
          />
          <Button
            type="submit"
            disabled={adding || !peerTag.trim()}
            data-testid="contact-tag-add-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
          </Button>
        </form>
        <p className="text-xs text-slate-500 leading-relaxed">
          Adding by tag is mutual — the moment you click Add, you appear in their contacts
          too. Only share your tag with people you actually want to hear from.
        </p>
      </div>
    </div>
  );
};

export default ContactTagSetting;
